import type { SubjectCreationFailedAction } from '@Application/Subjects/Actions/subjectCreationFailed';
import CreateSubject, { type SubjectAboutPayload } from '@Application/Subjects/Commands/CreateSubject';
import createSubject from '@Application/Subjects/createSubject';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import SubjectSheetFormData from '@UI/Pages/Subjects/Shared/FormData/SubjectSheetFormData';
import AboutField from '@UI/Pages/Subjects/Sidebars/CreateSubjectSidebar/AboutField';
import SheetFields from '@UI/Pages/Subjects/Sidebars/CreateSubjectSidebar/SheetFields';
import createSubjectSidebarSignal, {
	closeCreateSubjectSidebar,
} from '@UI/Pages/Subjects/Signals/createSubjectSidebarSignal';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type AboutCandidateView from '@UI/Store/Subjects/Views/AboutCandidateView';
import type SubjectTypeView from '@UI/Store/SubjectTypes/Views/SubjectTypeView';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch } from '@verclam/synapse';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { Button, Form, Message, Sidebar } from '@verclam/ui-kit';

type Props = {
	readonly type: SubjectTypeView;
};

const agencyNoticePt: React.ComponentProps<typeof Message>['pt'] = {
	root: { style: { marginBottom: 'var(--ui-kit-space-md)' } },
};

/**
 * Creation panel of a dossier of `type`, in the agency of work: the person it is about, the title and
 * the sheet fields of the type. The panel title is the type's own wording; once created, the dossier opens.
 */
const CreateSubjectSidebar: React.FC<Props> = ({ type }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isVisible = useSignalValue(createSubjectSidebarSignal);
	const { currentFacility } = useStrictCurrentFacility();

	const [sheet, setSheet] = React.useState(() => SubjectSheetFormData.createFor(type.fields));
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();

	React.useEffect(() => {
		if (isVisible) {
			setSheet(SubjectSheetFormData.createFor(type.fields));
		}
	}, [isVisible, type.fields]);

	const handleSheetChange = React.useCallback(
		(update: (current: SubjectSheetFormData) => SubjectSheetFormData) => {
			setSheet(update);
		},
		[],
	);

	const handleAboutChange = React.useCallback((candidate: AboutCandidateView | null) => {
		setSheet((current) => current.setAbout(candidate));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!sheet.isFormDataValid()) {
			setSheet(sheet.showErrors());
			return;
		}

		setIsSubmitting(true);

		const about =
			sheet.about === null
				? null
				: { kind: sheet.about.kind.value as SubjectAboutPayload['kind'], id: sheet.about.id };
		const command = new CreateSubject(
			currentFacility.id,
			type.id,
			sheet.title.title.trim(),
			sheet.toValues(),
			about,
		);

		dispatch(createSubject(command))
			.then((action) => {
				closeCreateSubjectSidebar();

				if (action.type === 'SUBJECT_CREATED') {
					navigate(buildSubjectPath(new SubjectAddress(type.key, action.payload.id)));
				}
			})
			.catch((action: SubjectCreationFailedAction) => {
				setSheet((current) => current.applyServerErrors(action.payload?.fieldErrors ?? []));
			})
			.finally(() => setIsSubmitting(false));
	}, [sheet, currentFacility.id, type.id, type.key, dispatch, navigate]);

	return (
		<Sidebar
			visible={isVisible}
			onHide={closeCreateSubjectSidebar}
			position="right"
			header={type.wording.createAction}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeCreateSubjectSidebar}
					/>
					<Button type="submit" form={formId} label="Créer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form id={formId} onSubmit={handleSubmit} aria-label={type.wording.createAction}>
				<Message
					severity="info"
					pt={agencyNoticePt}
					text={`Rattaché à l’agence ${currentFacility.name} — un dossier appartient à une agence et une seule.`}
				/>
				<SheetFields
					titleLabel={type.wording.titleLabel}
					sheet={sheet}
					onSheetChange={handleSheetChange}
					autoFocusTitle
				>
					<ConditionalLoader data={type.searchablePopulations.length > 0}>
						<AboutField
							label={type.wording.aboutLabel}
							facilityId={currentFacility.id}
							typeId={type.id}
							allowsExternal={type.allowsExternal}
							value={sheet.about}
							onChange={handleAboutChange}
							errorMessage={sheet.aboutServerMessage ?? undefined}
							isFormFieldValid={sheet.aboutServerMessage === null}
						/>
					</ConditionalLoader>
				</SheetFields>
			</Form>
		</Sidebar>
	);
};

export default CreateSubjectSidebar;
